// src/features/fourlink-config/utils/normalization/checklistNormalizers.js

import { firstNonEmpty, isMeaningful, normalizeValue, ensureArray } from '../helpers/filters';

export const normalizeChecklistItem = (item) => {
  if (item === null || item === undefined) {
    return null;
  }

  if (typeof item !== 'object') {
    const normalized = normalizeValue(item);
    if (normalized === undefined || normalized === null) {
      return null;
    }
    return { nombre: String(normalized) };
  }

  const candidateId = firstNonEmpty(
    item.id,
    item.checklist_id,
    item.fk_checklist,
    item.id_checklist,
    item.guid,
    item.uuid,
  );

  const candidateName = firstNonEmpty(
    item.nombre,
    item.name,
    item.titulo,
    item.descripcion,
    item.label,
  );

  const normalizedItem = { ...item };
  if (candidateId !== undefined && candidateId !== null) {
    normalizedItem.id = String(candidateId);
  }
  if (candidateName !== undefined && candidateName !== null) {
    normalizedItem.nombre = String(candidateName);
  }

  return normalizedItem;
};

export const normalizeChecklistCollection = (...sources) => {
  const aggregated = [];
  const seen = new Set();

  sources.forEach((source) => {
    ensureArray(source).forEach((rawItem) => {
      const item = normalizeChecklistItem(rawItem);
      if (!item) return;

      const key = item.id ? `id:${item.id}` : `name:${item.nombre || JSON.stringify(item)}`;
      if (seen.has(key)) return;
      seen.add(key);
      aggregated.push(item);
    });
  });

  return aggregated;
};

export const getSingleChecklistName = (checklist, fallback) => {
  if (!checklist || typeof checklist !== 'object') {
    return fallback || 'Ninguno';
  }
  return (
    checklist.nombre ||
    checklist.name ||
    checklist.titulo ||
    checklist.descripcion ||
    checklist.label ||
    (checklist.id ? `Checklist #${checklist.id}` : fallback || 'Con checklist')
  );
};

export const getChecklistDisplayName = (checklistSource, fallback) => {
  const normalizedCollection = normalizeChecklistCollection(checklistSource);
  if (normalizedCollection.length === 0) {
    return fallback || 'Ninguno';
  }

  if (normalizedCollection.length === 1) {
    return getSingleChecklistName(normalizedCollection[0], fallback);
  }

  const names = normalizedCollection
    .map((item) => getSingleChecklistName(item))
    .filter((name) => typeof name === 'string' && name.trim() && name.toLowerCase() !== 'con checklist');

  if (names.length === 0) {
    return `${normalizedCollection.length} checklists asignados`;
  }

  return names.join(', ');
};

export const getChecklistDescription = (checklist) => {
  if (!checklist || typeof checklist !== 'object') {
    return '';
  }

  const descriptionKeys = [
    'descripcion',
    'descripcion_larga',
    'description',
    'detalle',
    'detalle_checklist',
    'detalleChecklist',
    'detalle_check_list',
    'texto',
    'contenido',
    'observaciones',
    'nota',
    'notas',
  ];

  const candidates = descriptionKeys.map(key => checklist[key]).filter(Boolean);
  const description = firstNonEmpty(...candidates);

  if (description === undefined || description === null) {
    return '';
  }

  return typeof description === 'string' ? description : String(description);
};

export const normalizeChecklistIdentifier = (value) => {
  if (value === null || value === undefined) return '';
  const stringValue = String(value).trim();
  if (!stringValue) return '';

  const cleaned = stringValue
    .replace(/['\"`]/g, '')
    .replace(/[{}()[\]]/g, ' ')
    .replace(/\bfk[_-]?checklist\b\s*[:=]?/gi, ' ')
    .replace(/\bchecklist[_-]?id\b\s*[:=]?/gi, ' ')
    .replace(/\bid[_-]?checklist\b\s*[:=]?/gi, ' ')
    .replace(/\bchecklist\b\s*[:=]?/gi, ' ')
    .trim();

  if (!cleaned) return '';

  const parts = cleaned
    .split(/[;|,\/\s]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => !/^(null|undefined|ninguno|ninguna)$/i.test(part));

  if (parts.length === 0) {
    return '';
  }

  return parts[0];
};

export const collectChecklistIds = (...candidates) => {
  const ids = new Set();
  candidates.forEach((candidate) => {
    if (Array.isArray(candidate)) {
      candidate.forEach(item => {
        const normalized = normalizeChecklistIdentifier(item);
        if (normalized) ids.add(normalized);
      });
    } else {
      const normalized = normalizeChecklistIdentifier(candidate);
      if (normalized) ids.add(normalized);
    }
  });
  return Array.from(ids);
};